import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Skeleton } from "~/components/Skeleton";
import { DimensionalResponse } from "../types";
import { asCurrency, compactCurrency } from "../utils";
import { ChartTooltip } from "./ChartTooltip";

export interface UnitChartProps {
  data?: DimensionalResponse;
  isFetching: boolean;
}

const ROW_HEIGHT = 48;
const MIN_HEIGHT = 220;

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  "& .recharts-cartesian-grid-horizontal, & .recharts-cartesian-grid-vertical": {
    stroke: "$neutral-200",
  },
  "& .recharts-cartesian-axis-tick-value": {
    fill: "$neutrals-dark",
    fontSize: "$xs",
  },
});

// `fill` is a raw SVG attribute on recharts elements and cannot resolve
// Stitches `$token` references — use literal hex values.
const BAR_COLOR = "#0064c5";

export const UnitChart = ({ data, isFetching }: UnitChartProps) => {
  if (isFetching || !data) {
    return (
      <Card css={{ p: "$6" }}>
        <Skeleton variant="text-6" css={{ width: "40%" }} />
        <Skeleton css={{ width: "100%", height: "300px", mt: "$4" }} />
      </Card>
    );
  }

  if (data.data.length === 0) {
    return <EmptyState>Nenhum dado de gasto por unidade disponível para o período.</EmptyState>;
  }

  const chartData = [...data.data]
    .sort((a, b) => b.total - a.total)
    .map((item) => ({ name: item.name, total: item.total }));

  const chartHeight = Math.max(MIN_HEIGHT, chartData.length * ROW_HEIGHT);

  return (
    <Card css={{ p: "$6" }}>
      <div style={{ width: "100%", height: chartHeight }}>
        <StyledResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={compactCurrency}
            />
            <YAxis
              type="category"
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={180}
            />
            <Tooltip
              cursor={{ fill: "rgba(228, 228, 231, 0.4)" }}
              content={<ChartTooltip valueFormatter={asCurrency} />}
            />
            <Bar
              dataKey="total"
              name="Gasto Total"
              fill={BAR_COLOR}
              radius={[0, 4, 4, 0]}
              maxBarSize={32}
            />
          </BarChart>
        </StyledResponsiveContainer>
      </div>
    </Card>
  );
};

UnitChart.displayName = "UnitChart";
